import { useEffect } from "react";

/**
 * Cookieless Real User Monitoring.
 *
 * Collects Core Web Vitals (LCP, CLS, INP) for the initial page load and posts
 * them once, on page hide, to /api/public/rum. It sets no cookies, reads no
 * cookies, and writes nothing to localStorage/sessionStorage — so it needs no
 * cookie-consent banner. No IP address, user agent string, or identifier is
 * stored server-side; only a coarse device class and country/region.
 */

type Sample = {
  metric: "LCP" | "CLS" | "INP";
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  element?: string;
};

const THRESHOLDS: Record<Sample["metric"], [number, number]> = {
  LCP: [2500, 4000],
  CLS: [0.1, 0.25],
  INP: [200, 500],
};

function rate(metric: Sample["metric"], value: number): Sample["rating"] {
  const [good, poor] = THRESHOLDS[metric];
  if (value <= good) return "good";
  return value <= poor ? "needs-improvement" : "poor";
}

function deviceClass(): "mobile" | "tablet" | "desktop" {
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } })
    .userAgentData;
  const ua = navigator.userAgent;
  if (/iPad|Tablet|Nexus 7|SM-T/i.test(ua)) return "tablet";
  if (uaData?.mobile) return "mobile";
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
  return "desktop";
}

function describeElement(el: Element | null): string | undefined {
  if (!el) return undefined;
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : "";
  const cls =
    typeof el.className === "string" && el.className
      ? `.${el.className.trim().split(/\s+/).slice(0, 2).join(".")}`
      : "";
  return `${tag}${id}${cls}`.slice(0, 200);
}

export function RumBeacon() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("PerformanceObserver" in window)) return;
    // Skip local/preview traffic so field data reflects real visitors only.
    if (!/(^|\.)virginiabariatric\.com$|(^|\.)drfitzer\.com$/.test(location.hostname)) {
      return;
    }

    const samples = new Map<Sample["metric"], Sample>();
    const observers: PerformanceObserver[] = [];

    const observe = (
      type: string,
      cb: (entries: PerformanceEntryList) => void,
      opts: PerformanceObserverInit = {},
    ) => {
      try {
        const po = new PerformanceObserver((list) => cb(list.getEntries()));
        po.observe({ type, buffered: true, ...opts });
        observers.push(po);
      } catch {
        /* metric unsupported in this browser */
      }
    };

    observe("largest-contentful-paint", (entries) => {
      const last = entries[entries.length - 1] as
        | (PerformanceEntry & { element?: Element | null })
        | undefined;
      if (!last) return;
      samples.set("LCP", {
        metric: "LCP",
        value: Math.round(last.startTime),
        rating: rate("LCP", last.startTime),
        element: describeElement(last.element ?? null),
      });
    });

    let cls = 0;
    observe("layout-shift", (entries) => {
      for (const e of entries as Array<
        PerformanceEntry & { value: number; hadRecentInput: boolean }
      >) {
        if (!e.hadRecentInput) cls += e.value;
      }
      samples.set("CLS", {
        metric: "CLS",
        value: Math.round(cls * 1000) / 1000,
        rating: rate("CLS", cls),
      });
    });

    let inp = 0;
    observe(
      "event",
      (entries) => {
        for (const e of entries as Array<PerformanceEntry & { duration: number }>) {
          if (e.duration > inp) inp = e.duration;
        }
        if (inp > 0) {
          samples.set("INP", {
            metric: "INP",
            value: Math.round(inp),
            rating: rate("INP", inp),
          });
        }
      },
      { durationThreshold: 40 } as PerformanceObserverInit,
    );

    let sent = false;
    const flush = () => {
      if (sent) return;
      observers.forEach((po) => {
        try {
          po.takeRecords();
          po.disconnect();
        } catch {
          /* noop */
        }
      });
      if (samples.size === 0) return;
      sent = true;

      const nav = performance.getEntriesByType("navigation")[0] as
        | PerformanceNavigationTiming
        | undefined;
      const conn = (
        navigator as Navigator & { connection?: { effectiveType?: string } }
      ).connection;

      const payload = {
        path: location.pathname.slice(0, 300),
        lang: document.documentElement.lang || "en",
        device: deviceClass(),
        viewportWidth: window.innerWidth,
        connection: conn?.effectiveType ?? null,
        navigationType: nav?.type ?? null,
        samples: Array.from(samples.values()),
      };

      const body = JSON.stringify(payload);
      if (navigator.sendBeacon) {
        navigator.sendBeacon("/api/public/rum", new Blob([body], { type: "application/json" }));
      } else {
        void fetch("/api/public/rum", {
          method: "POST",
          body,
          headers: { "Content-Type": "application/json" },
          keepalive: true,
        }).catch(() => {});
      }
    };

    const onHidden = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", flush);
    // Safety net for visitors who linger: report after 20 s so slow-loading
    // sessions are captured even if the tab is never hidden.
    const timer = window.setTimeout(flush, 20000);

    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", flush);
      window.clearTimeout(timer);
      observers.forEach((po) => {
        try {
          po.disconnect();
        } catch {
          /* noop */
        }
      });
    };
  }, []);

  return null;
}
